import React from "react";
import { FaAndroid, FaApple, FaCalendarAlt, FaMapMarkerAlt } from "react-icons/fa";
import { useParams } from "react-router-dom";

import brand from "../../assets/brand.png";
import api from "../../services/api";
import {
  ActionButton,
  ActionHint,
  ActionIcon,
  ActionRow,
  ActionText,
  ActionTitle,
  BrandCopy,
  BrandLink,
  BrandMark,
  CardBadge,
  CardBody,
  CardCover,
  CardGrid,
  CardHeader,
  CompanyAvatar,
  CompanyMeta,
  CompanyName,
  CoverImage,
  CoverOverlay,
  EmptyCard,
  EventInfo,
  EventInfoLink,
  EventMeta,
  EventTitle,
  Footer,
  FooterLink,
  Glow,
  InfoRow,
  InfoText,
  LoadingCard,
  MetaRow,
  NavAnchor,
  NavLinks,
  Page,
  SecondaryButton,
  Shell,
  SummaryText,
  Tag,
  TagRow,
  TopBar,
  TopButton,
} from "./styles";

const APP_STORE_URL = "https://apps.apple.com/br/app/ondehj/id6764485556";

type EventDetails = {
  id: string;
  title: string;
  flyer: string;
  completeAddress: string;
  eventStartDate: string;
  info?: string | null;
  verified?: boolean;
  user: {
    id: string;
    name: string;
    avatar?: string;
    verified?: boolean;
  };
  tags: Array<{
    id: string;
    name: string;
  }>;
};

function formatEventDate(value: string) {
  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return "Data indisponivel";
  }

  return new Intl.DateTimeFormat("pt-BR", {
    weekday: "long",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(parsed);
}

function normalizeExternalLink(value: string) {
  const link = value.trim();

  if (!link) {
    return "";
  }

  if (link.startsWith("https://")) {
    return link;
  }

  return `https://${link}`;
}

function parseEventInfo(value?: string | null) {
  return String(value || "")
    .split("#")
    .map((element) => element.trim())
    .filter(Boolean)
    .map((element) => {
      if (element.startsWith("@@")) {
        return {
          type: "link" as const,
          value: normalizeExternalLink(element.replace(/^@@/, "")),
        };
      }

      return {
        type: "text" as const,
        value: element,
      };
    })
    .filter((item) => item.value);
}

export default function EventPage() {
  const { id = "" } = useParams();
  const [event, setEvent] = React.useState<EventDetails | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [errorMessage, setErrorMessage] = React.useState("");
  const infoItems = React.useMemo(() => parseEventInfo(event?.info), [event?.info]);

  React.useEffect(() => {
    let isMounted = true;

    const loadEvent = async () => {
      if (!id) {
        setErrorMessage("Evento nao encontrado.");
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setErrorMessage("");

      try {
        const { data } = await api.get<EventDetails>(`/events/${id}`);

        if (!isMounted) {
          return;
        }

        setEvent(data);
      } catch (error: any) {
        if (!isMounted) {
          return;
        }

        setErrorMessage(
          error?.response?.data?.message || "Nao foi possivel carregar este evento."
        );
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    loadEvent();

    return () => {
      isMounted = false;
    };
  }, [id]);

  return (
    <Page>
      <Glow />
      <Shell>
        <TopBar>
          <BrandLink href="/">
            <BrandMark alt="OndeHJ" src={brand} />
            <BrandCopy>Descubra o evento e baixe o app para acompanhar tudo no OndeHJ.</BrandCopy>
          </BrandLink>

          <NavLinks>
            <NavAnchor href="/">Inicio</NavAnchor>
            <NavAnchor href="/livemap">Premium</NavAnchor>
            <NavAnchor href="/support">Suporte</NavAnchor>
          </NavLinks>

          <TopButton href={APP_STORE_URL} rel="noreferrer" target="_blank">
            Baixar app
          </TopButton>
        </TopBar>

        {isLoading ? (
          <LoadingCard>Carregando evento...</LoadingCard>
        ) : null}

        {!isLoading && errorMessage ? (
          <EmptyCard>
            <strong>{errorMessage}</strong>
            <span>Abra o OndeHJ para ver mais eventos da cidade.</span>
            <ActionRow>
              <ActionButton href={APP_STORE_URL} rel="noreferrer" target="_blank">
                <ActionIcon>
                  <FaApple />
                </ActionIcon>
                <ActionText>
                  <ActionHint>Disponivel agora</ActionHint>
                  <ActionTitle>Baixar na App Store</ActionTitle>
                </ActionText>
              </ActionButton>
              <SecondaryButton as="span" aria-disabled="true">
                <ActionIcon>
                  <FaAndroid />
                </ActionIcon>
                <ActionText>
                  <ActionHint>Android</ActionHint>
                  <ActionTitle>Google Play em breve</ActionTitle>
                </ActionText>
              </SecondaryButton>
            </ActionRow>
          </EmptyCard>
        ) : null}

        {!isLoading && event ? (
          <>
            <CardGrid>
              <CardCover>
                <CoverImage alt={event.title} src={event.flyer} />
                <CoverOverlay />
              </CardCover>

              <CardBody>
                <CardHeader>
                  <CompanyAvatar
                    alt={event.user.name}
                    src={event.user.avatar || brand}
                  />
                  <CompanyMeta>
                    <CardBadge>Evento no OndeHJ</CardBadge>
                    <CompanyName>{event.user.name}</CompanyName>
                  </CompanyMeta>
                </CardHeader>

                <EventTitle>{event.title}</EventTitle>

                <MetaRow>
                  <EventMeta>{event.verified ? "Evento verificado" : "Evento publicado"}</EventMeta>
                </MetaRow>

                <InfoRow>
                  <FaCalendarAlt />
                  <InfoText>{formatEventDate(event.eventStartDate)}</InfoText>
                </InfoRow>

                <InfoRow>
                  <FaMapMarkerAlt />
                  <InfoText>{event.completeAddress}</InfoText>
                </InfoRow>

                {infoItems.map((item, index) =>
                  item.type === "link" ? (
                    <EventInfoLink
                      href={item.value}
                      key={`${item.type}-${index}`}
                      rel="noreferrer"
                      target="_blank"
                    >
                      Ver ingressos
                    </EventInfoLink>
                  ) : (
                    <EventInfo key={`${item.type}-${index}`}>- {item.value}</EventInfo>
                  )
                )}

                {event.tags.length > 0 ? (
                  <TagRow>
                    {event.tags.map((tag) => (
                      <Tag key={tag.id}>{tag.name}</Tag>
                    ))}
                  </TagRow>
                ) : null}

                <SummaryText>
                  Baixe o app para salvar favoritos, descobrir mais eventos e acompanhar a
                  cidade em tempo real.
                </SummaryText>

                <ActionRow>
                  <ActionButton href={APP_STORE_URL} rel="noreferrer" target="_blank">
                    <ActionIcon>
                      <FaApple />
                    </ActionIcon>
                    <ActionText>
                      <ActionHint>Disponivel agora</ActionHint>
                      <ActionTitle>Baixar na App Store</ActionTitle>
                    </ActionText>
                  </ActionButton>

                  <SecondaryButton as="span" aria-disabled="true">
                    <ActionIcon>
                      <FaAndroid />
                    </ActionIcon>
                    <ActionText>
                      <ActionHint>Android</ActionHint>
                      <ActionTitle>Google Play em breve</ActionTitle>
                    </ActionText>
                  </SecondaryButton>
                </ActionRow>
              </CardBody>
            </CardGrid>

            <Footer>
              <span>Evento exibido no OndeHJ com visual alinhado ao app.</span>
              <FooterLink href="/support">Falar com o suporte</FooterLink>
            </Footer>
          </>
        ) : null}
      </Shell>
    </Page>
  );
}
